import { useCallback, useEffect, useRef, useState } from 'react'
import { FileText, Loader2, X } from 'lucide-react'
import { meetingsAPI } from '../../services/api'
import { useToast } from '../common/Toast'

const POLL_MS = 2500
const MAX_POLL_TICKS = 120

function unwrapData(res) {
  return res?.data ?? res
}

function normalizeProcessingStatus(att) {
  const raw = att?.processingStatus ?? att?.processing_status
  if (raw === 'brief_ready' || raw === 'processed') return 'processed'
  if (raw === 'failed') return 'failed'
  if (raw === 'pending' || raw === 'extracting') return 'processing'
  return raw ? String(raw) : 'unknown'
}

const PROCESSING_ERROR_LABELS = {
  EXTRACTION_UNSUPPORTED_FORMAT: 'Unsupported file type',
  EXTRACTION_PDF_NO_TEXT: 'No readable text (scanned or image PDF)',
  EXTRACTION_PARSE_FAILED: 'Could not read this file',
  EXTRACTION_TEXT_TOO_SHORT: 'Too little text to process',
  PROCESS_INTERNAL_ERROR: 'Server could not process this file',
  BRIEF_OBJECT_ENCRYPT_FAILED: 'Could not secure extracted text',
  BRIEF_OBJECT_PUT_FAILED: 'Could not store extracted text',
}

function formatProcessingStatus(att) {
  const normalized = normalizeProcessingStatus(att)
  if (normalized === 'processed') return 'Ready'
  if (normalized === 'processing') return 'Processing'
  if (normalized === 'failed') {
    const code = att?.processingErrorCode ?? att?.processing_error_code
    if (code && PROCESSING_ERROR_LABELS[code]) {
      return `Failed — ${PROCESSING_ERROR_LABELS[code]}`
    }
    return 'Failed'
  }
  return normalized === 'unknown' ? 'Unknown' : normalized
}

function attachmentsNeedPolling(list) {
  return (list ?? []).some((att) => normalizeProcessingStatus(att) === 'processing')
}

/**
 * Attachment list for meeting builder (review summary and agenda step).
 * Optional soft-delete when `allowDelete` is set.
 */
export default function MeetingAttachmentsSummaryList({
  meetingId,
  listScope = 'meeting',
  allowDelete = false,
  onMutated,
}) {
  const toast = useToast()
  const [loading, setLoading] = useState(Boolean(meetingId))
  const [attachments, setAttachments] = useState([])
  const [deletingId, setDeletingId] = useState(null)
  const pollTicksRef = useRef(0)

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!meetingId) {
      setAttachments([])
      setLoading(false)
      return
    }
    if (!silent) {
      setLoading(true)
    }
    try {
      const res = await meetingsAPI.listAttachments(meetingId, listScope)
      const data = unwrapData(res)
      const list = data?.attachments ?? []
      setAttachments(Array.isArray(list) ? list : [])
    } catch {
      if (!silent) {
        setAttachments([])
      }
    } finally {
      if (!silent) {
        setLoading(false)
      }
    }
  }, [listScope, meetingId])

  useEffect(() => {
    pollTicksRef.current = 0
    void refresh()
  }, [refresh])

  useEffect(() => {
    if (!meetingId || !attachmentsNeedPolling(attachments)) {
      pollTicksRef.current = 0
      return undefined
    }

    const timer = window.setInterval(() => {
      pollTicksRef.current += 1
      if (pollTicksRef.current > MAX_POLL_TICKS) {
        window.clearInterval(timer)
        return
      }
      void refresh({ silent: true })
    }, POLL_MS)

    return () => window.clearInterval(timer)
  }, [attachments, meetingId, refresh])

  const handleDelete = async (att) => {
    if (!meetingId || !att?.id) return
    const label = att.filename || 'this document'
    if (!window.confirm(`Remove "${label}" from meeting documents?`)) return
    setDeletingId(att.id)
    try {
      await meetingsAPI.deleteAttachment(meetingId, att.id)
      toast.success('Document removed', label)
      await refresh()
      onMutated?.()
    } catch (err) {
      const message =
        typeof err?.message === 'string' && err.message.trim() ? err.message : 'Request failed.'
      toast.error('Remove failed', message)
    } finally {
      setDeletingId(null)
    }
  }

  if (!meetingId) {
    return <p className="text-base text-gray-500">None</p>
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-1 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
        Loading documents…
      </div>
    )
  }

  if (attachments.length === 0) {
    return <p className="text-base text-gray-500">None</p>
  }

  return (
    <ul className="space-y-2">
      {attachments.map((att) => {
        const status = formatProcessingStatus(att)
        const isProcessing = normalizeProcessingStatus(att) === 'processing'
        const isFailed = normalizeProcessingStatus(att) === 'failed'
        return (
          <li
            key={att.id}
            className="flex items-start gap-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-sm"
          >
            <FileText className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-gray-900">{att.filename || att.id}</span>
              <span
                className={`block text-xs ${isFailed ? 'text-red-600' : isProcessing ? 'text-amber-600' : 'text-gray-500'}`}
              >
                {isProcessing ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                    {status}
                  </span>
                ) : (
                  status
                )}
              </span>
            </span>
            {allowDelete ? (
              <button
                type="button"
                onClick={() => void handleDelete(att)}
                disabled={deletingId === att.id}
                className="shrink-0 rounded-full p-1 text-gray-400 transition hover:bg-white hover:text-gray-700 disabled:opacity-50"
                aria-label={`Remove ${att.filename || 'document'}`}
              >
                {deletingId === att.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <X className="h-4 w-4" />
                )}
              </button>
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}
